import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { UpdateAvailableBar } from './components/UpdateAvailableBar';
import * as Sentry from '@sentry/react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ModulePermissionsProvider } from './context/ModulePermissionsContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AppShell from './components/layout/AppShell';
import { ThemeProvider } from '@/components/theme-provider';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ToastProvider } from '@/components/ui-legacy';
import Login from './pages/Login';
import NotFound from './pages/NotFound';
import { lazy, Suspense } from 'react';

// Dev-only. The guard has to wrap the lazy() call, not just the <Route>:
// a bare top-level lazy(() => import(...)) is statically analysable, so Rollup
// emits the chunk even when the route is dead code. Inside the ternary the
// whole branch drops once import.meta.env.DEV is replaced with false.
const ShimHarness = import.meta.env.DEV
  ? lazy(() => import('./pages/__ShimHarness'))
  : null;

const Dashboard = lazy(() => import('./pages/Dashboard'));
const ClockIn = lazy(() => import('./pages/ClockIn'));
const FaceEnrol = lazy(() => import('./pages/FaceEnrol'));
const Requests = lazy(() => import('./pages/Requests'));
const History = lazy(() => import('./pages/History'));
const People = lazy(() => import('./pages/People'));
const StaffProfile = lazy(() => import('./pages/StaffProfile'));
const Leave = lazy(() => import('./pages/Leave'));
const MyInfo = lazy(() => import('./pages/MyInfo'));
const Reports = lazy(() => import('./pages/Reports'));
const Hiring = lazy(() => import('./pages/Hiring'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const CandidateProfile = lazy(() => import('./pages/CandidateProfile'));
const Performance = lazy(() => import('./pages/Performance'));
const ReviewForm = lazy(() => import('./pages/ReviewForm'));
const Compensation = lazy(() => import('./pages/Compensation'));
const Announcements = lazy(() => import('./pages/Announcements'));
const NotificationLogs = lazy(() => import('./pages/NotificationLogs'));
const SettingsPage = lazy(() => import('./pages/Settings'));
const PasswordChange = lazy(() => import('./pages/PasswordChange'));
const SessionsPage = lazy(() => import('./pages/Sessions'));
const TwoFactorPage = lazy(() => import('./pages/TwoFactor'));
const Login2FAPage = lazy(() => import('./pages/Login2FA'));
const NotificationPreferencesPage = lazy(() => import('./pages/NotificationPreferences'));
const AuditLogsPage = lazy(() => import('./pages/AuditLogs'));
const OffboardingListPage = lazy(() => import('./pages/OffboardingList'));
const OffboardingDetailPage = lazy(() => import('./pages/OffboardingDetail'));
const OffboardingMyTasksPage = lazy(() => import('./pages/OffboardingMyTasks'));
const Account = lazy(() => import('./pages/Account'));
const OnboardingPage = lazy(() => import('./pages/Onboarding'));
const AdminPermissions = lazy(() => import('./pages/AdminPermissions'));
const AdminUserManagement = lazy(() => import('./pages/AdminUserManagement'));
const SuperAdminTenants = lazy(() => import('./pages/SuperAdminTenants'));
const TenantPermissions = lazy(() => import('./pages/TenantPermissions'));
const Training = lazy(() => import('./pages/Training'));
const TrainingCourse = lazy(() => import('./pages/TrainingCourse'));
const TrainingLesson = lazy(() => import('./pages/TrainingLesson'));
const TrainingQuiz = lazy(() => import('./pages/TrainingQuiz'));
const OnboardingWizard = lazy(() => import('./pages/OnboardingWizard'));

// Sentry verification route (dev only). Renders a component that throws on
// mount so the App-level Sentry.ErrorBoundary fires and ships an event.
// Mounted below behind `import.meta.env.DEV` so it is TREE-SHAKEN out of
// production builds entirely — it literally cannot be navigated to in prod.
// Navigate to /__sentry-test-dev__ in local dev to trigger.
function SentryTestThrower(): never {
  throw new Error('Sentry frontend verification — this is a deliberate test throw.');
}

// ─── B9 redirect helpers ─────────────────────────────────────────────────────
// React Router's <Navigate to="/foo/:id"> doesn't expand :id automatically —
// params come from the CURRENT route match. This tiny component substitutes
// the params from useParams into the target path so old → new URL redirects
// that carry an id parameter work.
function RedirectWithParams({ to }: { to: string }) {
  const params = useParams();
  const expanded = Object.entries(params).reduce(
    (acc, [k, v]) => (v !== undefined ? acc.replaceAll(`:${k}`, v) : acc),
    to,
  );
  return <Navigate to={expanded} replace />;
}

// The old /reports URL now splits by role per B9:
//   hr_admin / it_admin  → /admin/reports
//   manager              → /people/team-reports
//   staff                → 403 (no reports surface)
function ReportsRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'hr_admin' || user.role === 'it_admin') {
    return <Navigate to="/admin/reports" replace />;
  }
  if (user.role === 'manager') return <Navigate to="/people/team-reports" replace />;
  return <Navigate to="/dashboard" replace />;
}

function RootRedirect() {
  // AuthContext handles the redirect; this is just a fallback
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />

      <Route path="/login" element={<Login />} />
      <Route
        path="/login/2fa"
        element={
          <Suspense fallback={null}>
            <Login2FAPage />
          </Suspense>
        }
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Suspense fallback={null}>
              <Dashboard />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* Every employee has shifts, so every role can clock in — this was gated to
          staff + manager, which left HR and IT admins with no way to record their own
          attendance. super_admin is excluded by ProtectedRoute's own path allowlist:
          they are a platform operator with no tenant, branch or shift.
          Access stays switchable per tenant through the 'clock_in' module key. */}
      <Route
        path="/clock-in"
        element={
          <ProtectedRoute
            allowedRoles={['staff', 'manager', 'hr_admin', 'it_admin']}
            moduleKey="clock_in"
          >
            <Suspense fallback={null}>
              <ClockIn />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/enrol-face"
        element={
          <ProtectedRoute allowedRoles={['staff', 'manager', 'hr_admin', 'it_admin']}>
            <Suspense fallback={null}>
              <FaceEnrol />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/requests"
        element={
          <ProtectedRoute allowedRoles={['staff', 'manager', 'hr_admin']}>
            <Suspense fallback={null}>
              <Requests />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <Suspense fallback={null}>
              <History />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* ═══ B9 — 6-tab IA ═══════════════════════════════════════════════════════
          Top-level tabs: Home · People · Time Off · Hiring · Performance & Pay · Admin.
          Personal surfaces (My Info, History) move to avatar dropdown.
          Announcements becomes Home widget + bell icon; authoring under /admin.
          See docs/conventions or CHANGELOG B9 entry for the full map.
       ══════════════════════════════════════════════════════════════════════════ */}

      {/* ── People hub ─────────────────────────────────────────────────────── */}
      {/* B9 fix #1: /people and /people/:id admit staff (directory-only).
          Offboarding / Onboarding / Team Reports sub-routes stay gated to
          manager + admins below. People.tsx reads the viewer's role and
          hides edit actions / bulk operations when the viewer is staff. */}
      <Route
        path="/people"
        element={
          <ProtectedRoute>
            <Suspense fallback={null}>
              <People />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/people/:id"
        element={
          <ProtectedRoute>
            <Suspense fallback={null}>
              <StaffProfile />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/people/offboarding"
        element={
          <ProtectedRoute allowedRoles={['hr_admin', 'it_admin']} moduleKey="offboarding">
            <Suspense fallback={null}>
              <OffboardingListPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      {/* Detail + my-tasks stay open to every role on purpose: the leaver reads
          their own workflow here, and anyone can be assigned a task in someone
          else's. The API enforces self-or-admin on the detail payload. They do
          carry the module key, so switching Offboarding off closes them too. */}
      <Route
        path="/people/offboarding/:id"
        element={
          <ProtectedRoute moduleKey="offboarding">
            <Suspense fallback={null}>
              <OffboardingDetailPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/people/offboarding/my-tasks"
        element={
          <ProtectedRoute moduleKey="offboarding">
            <Suspense fallback={null}>
              <OffboardingMyTasksPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/people/onboarding"
        element={
          <ProtectedRoute allowedRoles={['manager', 'hr_admin', 'it_admin']} moduleKey="onboarding">
            <Suspense fallback={null}>
              <OnboardingPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      {/* Managers have no Admin tab, so Team Reports is their reporting surface —
          same 'reports' module key the Admin routes use, so one toggle governs both. */}
      <Route
        path="/people/team-reports"
        element={
          <ProtectedRoute allowedRoles={['manager']} moduleKey="reports">
            <Suspense fallback={null}>
              <Reports />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* ── Time Off hub ───────────────────────────────────────────────────── */}
      {/* it_admin is included on these three: getNavItems already gives it_admin a
          "Time Off" sidebar entry pointing at /time-off, and hubTabs already renders
          My Leave + Calendar for it. Without it here those links dead-ended in the
          fullscreen ForbiddenState. Approvals and Other Requests stay excluded. */}
      <Route
        path="/time-off"
        element={
          <ProtectedRoute allowedRoles={['staff', 'manager', 'hr_admin', 'it_admin']}>
            <Suspense fallback={null}>
              <Leave />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-off/my-leave"
        element={
          <ProtectedRoute allowedRoles={['staff', 'manager', 'hr_admin', 'it_admin']}>
            <Suspense fallback={null}>
              <Leave />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-off/calendar"
        element={
          <ProtectedRoute allowedRoles={['staff', 'manager', 'hr_admin', 'it_admin']}>
            <Suspense fallback={null}>
              <Leave />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-off/approvals"
        element={
          <ProtectedRoute allowedRoles={['manager', 'hr_admin']}>
            <Suspense fallback={null}>
              <Leave />
            </Suspense>
          </ProtectedRoute>
        }
      />
      {/* B9 fix #3 — Other Requests (WFH + travel). Re-parented from the
          old top-level /requests URL so it has a nav entry point via the
          Time Off hub. Underlying component is unchanged — just mounted
          here + gets the Time Off HubTabBar. Old /requests URL redirects
          to this path below. */}
      <Route
        path="/time-off/other-requests"
        element={
          <ProtectedRoute allowedRoles={['staff', 'manager', 'hr_admin']}>
            <Suspense fallback={null}>
              <Requests />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* ── Hiring (stays top-level — recruiters shouldn't drill through People) ── */}
      <Route
        path="/hiring"
        element={
          <ProtectedRoute allowedRoles={['manager', 'hr_admin']} customCheck={(u) => u.is_recruiter} moduleKey="hiring">
            <Suspense fallback={null}>
              <Hiring />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hiring/jobs/:id"
        element={
          <ProtectedRoute allowedRoles={['manager', 'hr_admin']} customCheck={(u) => u.is_recruiter} moduleKey="hiring">
            <Suspense fallback={null}>
              <JobDetail />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hiring/candidates/:id"
        element={
          <ProtectedRoute allowedRoles={['manager', 'hr_admin']} customCheck={(u) => u.is_recruiter} moduleKey="hiring">
            <Suspense fallback={null}>
              <CandidateProfile />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* ── Performance & Pay hub ──────────────────────────────────────────── */}
      <Route
        path="/performance-pay"
        element={
          <ProtectedRoute allowedRoles={['staff', 'manager', 'hr_admin']}>
            <Suspense fallback={null}>
              <Performance />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/performance-pay/performance"
        element={
          <ProtectedRoute allowedRoles={['staff', 'manager', 'hr_admin']}>
            <Suspense fallback={null}>
              <Performance />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/performance-pay/performance/cycles/:cycleId"
        element={
          <ProtectedRoute allowedRoles={['manager', 'hr_admin']}>
            <Suspense fallback={null}>
              <Performance />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/performance-pay/performance/reviews/:id"
        element={
          <ProtectedRoute allowedRoles={['staff', 'manager', 'hr_admin']}>
            <Suspense fallback={null}>
              <ReviewForm />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/performance-pay/compensation"
        element={
          <ProtectedRoute allowedRoles={['manager', 'hr_admin']}>
            <Suspense fallback={null}>
              <Compensation />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* ── Admin hub (hr_admin + it_admin only) ───────────────────────────── */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['hr_admin', 'it_admin']} moduleKey="reports">
            <Suspense fallback={null}>
              <Reports />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reports"
        element={
          <ProtectedRoute allowedRoles={['hr_admin', 'it_admin']} moduleKey="reports">
            <Suspense fallback={null}>
              <Reports />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/audit-logs"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'it_admin']}>
            <Suspense fallback={null}>
              <AuditLogsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/permissions"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <Suspense fallback={null}>
              <AdminPermissions />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tenants"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <Suspense fallback={null}>
              <SuperAdminTenants />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tenants/:id/permissions"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <Suspense fallback={null}>
              <TenantPermissions />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute allowedRoles={['hr_admin', 'it_admin']}>
            <Suspense fallback={null}>
              <SettingsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/announcements"
        element={
          <ProtectedRoute allowedRoles={['hr_admin', 'it_admin']}>
            <Suspense fallback={null}>
              <Announcements />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/notification-logs"
        element={
          <ProtectedRoute allowedRoles={['it_admin', 'hr_admin']}>
            <Suspense fallback={null}>
              <NotificationLogs />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/user-management"
        element={
          <ProtectedRoute allowedRoles={['hr_admin', 'it_admin', 'super_admin']}>
            <Suspense fallback={null}>
              <AdminUserManagement />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* ── Personal surfaces (reached via avatar dropdown) ────────────────── */}
      <Route
        path="/my-info"
        element={
          <ProtectedRoute>
            <Suspense fallback={null}>
              <MyInfo />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/history"
        element={
          <ProtectedRoute>
            <Suspense fallback={null}>
              <History />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* ── Non-sidebar destination routes (deep-linked from widgets etc.) ─── */}
      <Route
        path="/announcements"
        element={
          <ProtectedRoute>
            <Suspense fallback={null}>
              <Announcements />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* ── Training LMS ───────────────────────────────────────────────────── */}
      <Route
        path="/training"
        element={
          <ProtectedRoute allowedRoles={['staff', 'manager', 'hr_admin', 'it_admin']}>
            <Suspense fallback={null}>
              <Training />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/training/:id"
        element={
          <ProtectedRoute allowedRoles={['staff', 'manager', 'hr_admin', 'it_admin']}>
            <Suspense fallback={null}>
              <TrainingCourse />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/training/:id/lesson/:lessonId"
        element={
          <ProtectedRoute allowedRoles={['staff', 'manager', 'hr_admin', 'it_admin']}>
            <Suspense fallback={null}>
              <TrainingLesson />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/training/:id/quiz/:quizId"
        element={
          <ProtectedRoute allowedRoles={['staff', 'manager', 'hr_admin', 'it_admin']}>
            <Suspense fallback={null}>
              <TrainingQuiz />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* ── Onboarding wizard ──────────────────────────────────────────────────── */}
      <Route
        path="/onboarding-wizard"
        element={
          <ProtectedRoute>
            <Suspense fallback={null}>
              <OnboardingWizard />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* ── B9 redirects — every OLD URL lands on its new home via Navigate replace ─ */}
      {/* Leave */}
      <Route path="/leave"                element={<Navigate to="/time-off/my-leave" replace />} />
      {/* B9 fix #3 — /requests re-parented as Time Off / Other Requests sub-tab.
          Structural merge of WFH approvals into /time-off/approvals (leave
          approvals) remains deferred (different UX flows). But /requests
          now has a sidebar entry point via Time Off, was previously orphaned
          after the sidebar consolidation. */}
      <Route path="/requests" element={<Navigate to="/time-off/other-requests" replace />} />
      {/* History moved to avatar dropdown */}
      <Route path="/history"              element={<Navigate to="/account/history" replace />} />
      {/* Performance + Compensation */}
      <Route path="/performance"                        element={<Navigate to="/performance-pay/performance" replace />} />
      <Route path="/performance/cycles/:cycleId"        element={<RedirectWithParams to="/performance-pay/performance/cycles/:cycleId" />} />
      <Route path="/performance/reviews/:id"            element={<RedirectWithParams to="/performance-pay/performance/reviews/:id" />} />
      <Route path="/compensation"                        element={<Navigate to="/performance-pay/compensation" replace />} />
      {/* Offboarding */}
      <Route path="/hr/offboarding"                      element={<Navigate to="/people/offboarding" replace />} />
      <Route path="/hr/offboarding/:id"                  element={<RedirectWithParams to="/people/offboarding/:id" />} />
      <Route path="/offboarding/my-tasks"                element={<Navigate to="/people/offboarding/my-tasks" replace />} />
      {/* Audit Logs */}
      <Route path="/it-admin/audit-logs"                 element={<Navigate to="/admin/audit-logs" replace />} />
      {/* Reports — fans out by role (admins → /admin, managers → /people/team-reports, staff → 403) */}
      <Route path="/reports"                              element={<ReportsRedirect />} />

      {/* /clock-in is declared once, near the top with the other real pages. A second
          copy used to sit here; React Router matched the first and this one never ran,
          so widening the roles on it alone would have changed nothing. */}

      {/*
        B1 — Account hub.
        /account is the landing page with sections linking to security /
        preferences pages below. The individual pages (PasswordChange,
        Sessions, TwoFactor, NotificationPreferences) are re-mounted under
        /account/* so that's the canonical URL. The older /profile/security/*
        and /settings/notifications URLs still resolve — they just render
        a <Navigate replace> so any bookmarked deep link lands on the new path.
      */}
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <Suspense fallback={null}>
              <Account />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/security/password"
        element={
          <ProtectedRoute>
            <Suspense fallback={null}>
              <PasswordChange />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/security/sessions"
        element={
          <ProtectedRoute>
            <Suspense fallback={null}>
              <SessionsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/security/2fa"
        element={
          <ProtectedRoute>
            <Suspense fallback={null}>
              <TwoFactorPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/preferences/notifications"
        element={
          <ProtectedRoute>
            <Suspense fallback={null}>
              <NotificationPreferencesPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* Legacy URL redirects — the old paths still work but land on /account/*.
          Using <Navigate replace> so browser history doesn't keep the stale URL. */}
      <Route path="/profile/security/password" element={<Navigate to="/account/security/password" replace />} />
      <Route path="/profile/security/sessions" element={<Navigate to="/account/security/sessions" replace />} />
      <Route path="/profile/security/2fa"      element={<Navigate to="/account/security/2fa" replace />} />
      <Route path="/settings/notifications"    element={<Navigate to="/account/preferences/notifications" replace />} />

      {/* /hr/users/:id/sessions is admin-scoped (HR/IT viewing someone else's
          sessions), distinct from the per-user /account path — keeps its URL. */}
      <Route
        path="/hr/users/:id/sessions"
        element={
          <ProtectedRoute allowedRoles={['hr_admin', 'it_admin']}>
            <Suspense fallback={null}>
              <SessionsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/it-admin/audit-logs"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'it_admin']}>
            <Suspense fallback={null}>
              <AuditLogsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/hr/offboarding"
        element={
          <ProtectedRoute allowedRoles={['hr_admin', 'it_admin']}>
            <Suspense fallback={null}>
              <OffboardingListPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/hr/offboarding/:id"
        element={
          <ProtectedRoute>
            <Suspense fallback={null}>
              <OffboardingDetailPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/offboarding/my-tasks"
        element={
          <ProtectedRoute>
            <Suspense fallback={null}>
              <OffboardingMyTasksPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {import.meta.env.DEV && (
        <Route path="/__sentry-test-dev__" element={<SentryTestThrower />} />
      )}

      {/* Dev-only shim harness — exercises every ui-legacy primitive without a
          backend. Driven by scripts/verify-shims.mjs. Tree-shaken from prod. */}
      {ShimHarness && <Route path="/__shims__" element={<ShimHarness />} />}

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    // Theme is route- and auth-independent, so it wraps everything.
    <ThemeProvider>
      {/* Radix Tooltip.Root throws without a provider above it. Mounted now,
          before the first consumer, so the chrome batch cannot trip over it. */}
      <TooltipProvider>
        <BrowserRouter>
          <AuthProvider>
            <ModulePermissionsProvider>
              <ToastProvider>
                {/* One AppShell for the whole app, not one per page. Each page
                    used to render its own <AppShell>, so the shell remounted on
                    every navigation and restarted all six of its polling
                    intervals. It sits inside AuthProvider/Router because it
                    reads useAuth + useLocation, and it renders bare children
                    on the auth/full-screen routes (see SHELL_LESS_PATHS). */}
                <AppShell>
                  <AppRoutes />
                </AppShell>
                {/* Never reloads by itself: shows a bar when a newer version has been installed. */}
                <UpdateAvailableBar />
              </ToastProvider>
            </ModulePermissionsProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  );
}

// Matches Sentry's FallbackRender signature. `error` is typed `unknown` at the
// Sentry API boundary — narrow to a readable shape inside the component.
function ErrorFallback({ error }: { error: unknown; componentStack: string; eventId: string; resetError(): void }) {
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--background)',
        flexDirection: 'column',
        gap: 12,
        padding: 24,
      }}
    >
      <p style={{ color: 'var(--destructive)', fontFamily: 'var(--font-body)', fontWeight: 600 }}>
        Something went wrong. Please refresh and try again.
      </p>
      <pre style={{
        color: 'var(--muted-foreground)', fontFamily: 'monospace', fontSize: 12,
        background: 'var(--muted)', padding: 12, borderRadius: 8,
        maxWidth: 700, overflowX: 'auto', whiteSpace: 'pre-wrap',
      }}>
        {message}
        {stack && (
          <>
            {'\n'}
            {stack}
          </>
        )}
      </pre>
    </div>
  );
}

export default Sentry.withErrorBoundary(App, {
  fallback: ErrorFallback,
});
